import { useState, useEffect } from 'react';
import { Lock, LockOpen, Timer, LogOut, AlertCircle, FolderPlus, Loader2, Plus, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useVault } from './VaultContext';
import { VaultUnlockSheet } from './VaultUnlockSheet';
import { VaultItemsList } from './VaultItemsList';
import { AddItemSheet } from './AddItemSheet';
import { SendListTab } from './SendListTab';
import { toast } from 'sonner';

interface VaultSheetProps {
  clientId: number;
  clientName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VaultSheet({ clientId, clientName, open, onOpenChange }: VaultSheetProps) {
  const {
    isUnlocked,
    lock,
    findCollectionByClientId,
    getItemsByClientId,
    createCollectionForClient,
    timeRemaining,
  } = useVault();

  const [showUnlock, setShowUnlock] = useState(false);
  const [activeTab, setActiveTab] = useState<'passwords' | 'sends'>('passwords');
  const [showAddItem, setShowAddItem] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Find collection by client ID using naming convention "[clientId]"
  const collection = isUnlocked ? findCollectionByClientId(clientId) : null;
  const filteredItems = isUnlocked ? getItemsByClientId(clientId) : [];

  const handleCreateCollection = async () => {
    setIsCreating(true);
    try {
      await createCollectionForClient(clientId, clientName);
      toast.success('Коллекция создана');
    } catch (error) {
      toast.error('Ошибка создания коллекции', {
        description: error instanceof Error ? error.message : 'Неизвестная ошибка',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Auto-show unlock if vault is locked
  useEffect(() => {
    if (open && !isUnlocked && !showUnlock) {
      setShowUnlock(true);
    }
  }, [open, isUnlocked, showUnlock]);

  // Close unlock sheet when vault is unlocked
  useEffect(() => {
    if (isUnlocked) {
      setShowUnlock(false);
    }
  }, [isUnlocked]);

  // When unlock sheet is closed and vault is still locked, close main sheet too
  const handleUnlockSheetChange = (newOpen: boolean) => {
    setShowUnlock(newOpen);
    // If user explicitly closed unlock sheet and vault is still locked, close everything
    if (!newOpen && !isUnlocked) {
      onOpenChange(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Close unlock sheet when main sheet closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setShowUnlock(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="bottom" className="h-[80vh] flex flex-col">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle className="flex items-center gap-2">
              {isUnlocked ? (
                <LockOpen className="h-5 w-5 text-green-500" />
              ) : (
                <Lock className="h-5 w-5" />
              )}
              Пароли: {clientName}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {/* Vault locked */}
            {!isUnlocked && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Lock className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  Хранилище заблокировано
                </p>
                <Button onClick={() => setShowUnlock(true)}>
                  <Lock className="h-4 w-4 mr-2" />
                  Разблокировать
                </Button>
              </div>
            )}

            {/* Tabs — visible after unlock */}
            {isUnlocked && (
              <>
                <div className="flex border-b mb-3">
                  <button
                    className={`flex-1 py-2 text-sm font-medium text-center border-b-2 transition-colors ${
                      activeTab === 'passwords' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'
                    }`}
                    onClick={() => setActiveTab('passwords')}
                  >
                    Пароли
                  </button>
                  <button
                    className={`flex-1 py-2 text-sm font-medium text-center border-b-2 transition-colors ${
                      activeTab === 'sends' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'
                    }`}
                    onClick={() => setActiveTab('sends')}
                  >
                    <span className="inline-flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Send-ы</span>
                  </button>
                </div>

                {activeTab === 'sends' ? (
                  <SendListTab />
                ) : (
                  <>
                    {/* No collection for this client */}
                    {!collection ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-muted-foreground mb-2">Коллекция не найдена</p>
                        <p className="text-xs text-muted-foreground mb-4">
                          Будет создана: {clientName} [{clientId}]
                        </p>
                        <Button onClick={handleCreateCollection} disabled={isCreating}>
                          {isCreating ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Создание...</>
                          ) : (
                            <><FolderPlus className="h-4 w-4 mr-2" /> Создать коллекцию</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <>
                        {filteredItems.length > 0 ? (
                          <VaultItemsList items={filteredItems} />
                        ) : (
                          <div className="flex flex-col items-center justify-center py-4 text-center">
                            <p className="text-muted-foreground">Нет паролей в коллекции</p>
                          </div>
                        )}
                        <div className="mt-4 flex justify-center">
                          <Button variant="outline" onClick={() => setShowAddItem(true)}>
                            <Plus className="h-4 w-4 mr-2" /> Добавить запись
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer with session info */}
          {isUnlocked && (
            <div className="flex-shrink-0 border-t pt-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Timer className="h-4 w-4" />
                <span>
                  {timeRemaining !== null
                    ? `Сессия: ${formatTime(timeRemaining)}`
                    : 'Сессия активна'}
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={lock}>
                <LogOut className="h-4 w-4 mr-2" />
                Заблокировать
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Unlock sheet */}
      <VaultUnlockSheet open={showUnlock} onOpenChange={handleUnlockSheetChange} />

      {/* Add item sheet */}
      {collection && (
        <AddItemSheet
          collectionId={collection.id}
          open={showAddItem}
          onOpenChange={setShowAddItem}
        />
      )}
    </>
  );
}
